import Vue from 'vue';
import Component from 'vue-class-component';
import { eventBus } from '@/main';
import store from '@/store';
import { TableStateConfig } from '@/store/modules/tables-state';
import { isEqual } from 'lodash';

export enum SortOrder {
  asc = 'asc',
  desc = 'desc'
}

// You can declare mixins as the same style as components.
@Component
export class TableMixin extends Vue {
  keyValue: string = 'default';
  isDataFetched: boolean = false;

  // sort
  currentSort: string | null = null;
  currentSortOrder: string | null = SortOrder.asc;
  secondarySort: string | null = null;

  // pagination
  currentPage: number = 1;
  perPage: number = 20;
  total: number = 0;

  // filters
  filters: { [key: string]: string } | null = null;
  plainFilters: { [key: string]: string } | null = null;

  // search
  searchValue: string = '';
  searchName: string = 'search';

  // for table state management
  tableStateConfig: TableStateConfig = {
    actionName: '',
    getterName: ''
  };

  created(): void {
    eventBus.$on('window-resized', this.onWindowResize);
  }

  onWindowResize(): void {
    setTimeout((): void => {
      this.updateKeyValue();
    }, 500); // for table title row re-rendering
  }

  onSort(sortBy: string): void {
    if (this.currentSort === sortBy) {
      this.toggleSortOrder();
    } else {
      this.currentSort = sortBy;
      this.currentSortOrder = SortOrder.asc;
    }
    this.currentPage = 1;

    this.updateTablesState();
    this.fetchData();
  }

  toggleSortOrder(): void {
    if (this.currentSortOrder === SortOrder.asc) {
      this.currentSortOrder = SortOrder.desc;
    } else {
      this.currentSortOrder = SortOrder.asc;
    }

    this.updateTablesState();
  }

  onGoToPrevPage(): void {
    this.currentPage--;
    this.updateTablesState();

    this.fetchData();
  }

  onGoToNextPage(): void {
    this.currentPage++;
    this.updateTablesState();

    this.fetchData();
  }

  onFiltersParamsChanged(filters: { [key: string]: string }): void {
    if (!isEqual(this.filters, filters)) {
      this.currentPage = 1;
    }

    this.filters = filters;

    this.updateTablesState();
    this.fetchData();
  }

  onPlainFiltersParamsChanged(plainFilters: { [key: string]: string }): void {
    this.plainFilters = plainFilters;

    this.updateTablesState();
  }

  onSearchValueChanged(value: string, searchName: string = 'search'): void {
    this.searchValue = value;
    this.searchName = searchName;
    this.currentPage = 1;

    this.updateTablesState();
    // this.fetchData();
  }

  applySearch(): void {
    this.fetchData();
  }

  fetchData(): void {
    // console.log(this.searchValue);
    // method used in all tables for fetching data
  }

  get fetchParams(): { [key: string]: string | number } {
    let params: { [key: string]: string | number } = {};

    if (this.currentSort) {
      const prefix: string = this.currentSortOrder === SortOrder.asc ? '' : '-';
      params.sort = `${prefix}${this.currentSort}`;

      if (this.secondarySort) {
        params.sort = `${params.sort},${this.secondarySort}`;
      }
    }

    if (this.currentPage) {
      params.page = this.currentPage;
    }

    if (this.filters) {
      params = { ...params, ...this.filters };
    }

    if (this.searchValue) {
      params[`filter[${this.searchName}]`] = this.searchValue;
    } else {
      if (Object.keys(params).includes(`filter[${this.searchName}]`)) {
        delete params[`filter[${this.searchName}]`];
      }
    }

    return params;
  }

  updateTablesState(): void {
    if (this.tableStateConfig.actionName) {
      store.dispatch(this.tableStateConfig.actionName, {
        searchValue: this.searchValue,
        searchName: this.searchName,
        currentPage: this.currentPage,
        currentSort: this.currentSort,
        secondarySort: this.secondarySort,
        currentSortOrder: this.currentSortOrder,
        filters: this.filters,
        plainFilters: this.plainFilters
      });
    }
  }

  getTableState(): void {
    if (this.tableStateConfig.getterName) {
      this.currentSort = store.getters[this.tableStateConfig.getterName].currentSort;
      this.secondarySort = store.getters[this.tableStateConfig.getterName].secondarySort;
      this.currentSortOrder = store.getters[this.tableStateConfig.getterName].currentSortOrder;
      this.searchValue = store.getters[this.tableStateConfig.getterName].searchValue;
      this.searchName = store.getters[this.tableStateConfig.getterName].searchName;
      this.currentPage = store.getters[this.tableStateConfig.getterName].currentPage;
      this.filters = store.getters[this.tableStateConfig.getterName].filters;
      this.plainFilters = store.getters[this.tableStateConfig.getterName].plainFilters;
    }
  }

  onDataFetchCompleted(): void {
    this.updateKeyValue();
    this.isDataFetched = true;
  }

  updateKeyValue(): void {
    this.keyValue = (Math.random() * 1000).toString();
  }

  onRowClicked(routeName: string, paramName: string | string[], paramId: string | string[]): void {
    const params: { [key: string]: string } = {};
    if (Array.isArray(paramName) && Array.isArray(paramId)) {
      paramName.forEach((name: string, index: number): void => {
        params[name] = paramId[index];
      });
    } else if (typeof paramName === 'string' && typeof paramId === 'string') {
      params[paramName as string] = paramId as string;
    }

    this.$router.push({ name: routeName, params });
  }

  beforeDestroy(): void {
    eventBus.$off('window-resized', this.onWindowResize);
  }
}
